import type { DragEvent } from 'react';
import { Fragment, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useSidebarContext } from '@user/Sidebar';
import { useTranslation } from 'react-i18next';
import { useActionLogSender } from '@framework/action-log';
import { getClientId } from '@framework/app';
import { container } from '@framework/container';
import { symbols } from '@framework/container/symbols';
import { useViewModelOperationLogSender } from '@model-framework/action-log';
import { CommandHelper, CommandManagerContext, CompositeCommand, useCommandManagerRef } from '@model-framework/command';
import { DisplayOrderRepository } from '@model-framework/display-order/infrastructure';
import type { ConsistencyLinkId, ModelId, ViewId } from 'balus-common';
import { useCurrentUserPublicProfile } from '@user/PublicProfile/useUserPublicProfile';
import { ViewModelToolbar } from '@user/pages/ViewModelPage/FloatingUIContainer/ViewModelToolbar';
import { useWriteViewer, useFollowingViewportSync, FollowingIndicator, useFollowingUser } from '@view-model/Viewer';
import { followingUserIdAtom } from '@view-model/Viewer/followingUserAtom';
import { ConsistencyLinksOperation, StickyModelContentsOperation } from '@view-model/adapter';
import type { ApplicationClipboardPayload, ViewsClipboardPayload } from '@view-model/application/clipboard';
import { isViewsClipboardPayload } from '@view-model/application/clipboard';
import type { ViewModelPermissions } from '@view-model/application/context';
import { ClientMouseCursorPubSub } from '@view-model/application/mouse-cursor/ClientMouseCursorPubSub';
import type { HuddleMessage } from '@view-model/application/notification';
import { ViewModelNotification } from '@view-model/application/notification';
import { ViewModelPageOperation } from '@view-model/application/operation';
import type { WindowEventManager } from '@view-model/application/shortcuts';
import type { ModelCollection } from '@view-model/domain/model/ModelCollection';
import { StickyModel } from '@view-model/domain/model/StickyModel';
import type { ViewEntity } from '@view-model/domain/view/ViewEntity';
import { ViewCollection } from '@view-model/domain/view/ViewCollection';
import type { ViewModelEntity } from '@view-model/domain/view-model';
import { StickyModelElementPositionMapRepository } from '@view-model/infrastructure/basic-model/StickyModelElementPositionMapRepository';
import { ModelRepository } from '@view-model/infrastructure/view-model/ModelRepository';
import { Point, Size } from '@view-model/models/common/basic';
import { Rect } from '@view-model/models/common/basic/Rect';
import { LinkCreator, LinkerCanvasView } from '@view-model/models/common/components/LinkCreator';
import { LinkerState } from '@view-model/models/common/components/LinkCreator/LinkerState';
import type { Position } from '@view-model/models/common/types/ui';
import { LinkEntityOperation } from '@view-model/models/sticky/StickyLink/adapter/LinkEntityOperation';
import { LinkRepository } from '@view-model/models/sticky/StickyLink';
import { NodeEntityOperation, NodeRepository } from '@view-model/models/sticky/StickyNodeView';
import { StickyZoneEntityOperation, StickyZoneRepository } from '@view-model/models/sticky/StickyZoneView';
import { NewElementStyleProvider, NewElementStyleProviderContext } from '@view-model/models/sticky/user-context';
import { useOpenCloseState, View as ViewView } from '@view-model/ui/components/View';
import { AIWorkingViewEffect } from './AIModelingEffect/AIWorkingViewEffect';
import { TITLE_BAR_HEIGHT, TITLE_BAR_MARGIN } from '@view-model/ui/components/View/ViewTitleBar/constants';
import {
  ClientMouseCursorView,
  CONSISTENCY_LINK_LINE_WIDTH,
  ConsistencyLinkCollectionView,
  EmptyMessageModal,
  ViewModelPaste,
} from '@view-model/ui/components/ViewModel';
import type { WorkspaceEntity } from '@workspace/domain/workspace';
import { useNavigate, useLocation } from 'react-router';
import { toast } from 'react-hot-toast';
import { FloatingUIContainer } from './FloatingUIContainer';
import { AIAssistantDrawer } from './AIAssistant/AIAssistantDrawer';
import { AIAssistantOperationProvider } from './AIAssistantOperationContext';
import { useCreatingComments } from './hooks';
import { useContentViewBox, useProvideContentViewBox } from './useContentViewBox';
import { StickyModelContentsOperationContextProvider } from '@view-model/ui/components/Model';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useHandler } from '@framework/hooks';
import { useSnapshot } from '@framework/hooks/useSnapshot';
import { VisibleAreaCenterPointProvider } from './VisibleAreaCenterPointProvider';
import { ViewModelSVGRefContextProvider } from './ViewModelSVGRefContext';
import { RTDBPath, VIEW_MODEL_CANVAS_SIZE } from 'balus-common';
import { recordCalibrationEvent } from '@framework/utils/operationCalibration';
import { PositionSet, PositionSetUpdateCommand } from '@view-model/models/common/PositionSet';
import { PositionSetRepository } from '@view-model/models/common/PositionSet/infrastructure/PositionSetRepository';
import { ObjectRepository, RecordRepository } from '@framework/repository';
import { FileContainer } from '@view-model/models/sticky/FileContainer/domain/FileContainer';
import { FileContainerAssetTrashCommand } from '@view-model/models/sticky/FileContainer/command/FileContainerAssetTrashCommand';
import { useViewModelPageContentShortcuts } from '@user/pages/ViewModelPage/useViewModelPageContentShortcuts';
import { MultiSelectionMode } from './MultiSelectionMode';
import { useAtom } from 'jotai/react';
import { contentTransformAtom } from './contentTransformAtom';
import type { ConsistencyLinkCollectionJSON } from '@view-model/domain/ConsistencyLink/ConsistencyLinkCollection';
import { ConsistencyLinkCollection } from '@view-model/domain/ConsistencyLink/ConsistencyLinkCollection';
import { ViewsClipboardOperator } from '@view-model/application/clipboard/ViewsClipboardOperator';
import { PopupMenuTeleporter, RightClickMenuTeleporter, ViewTitleBarTeleporter } from './teleporters';
import { ViewsDeleteConfirmModal } from '@view-model/ui/components/View/ViewsDeleteConfirmModal';
import { VIEW_GRID_SIZE, viewGridLayout } from '@view-model/ui/components/View/constants';
import { ViewContextProvider } from '@view-model/ui/components/View/ViewContextProvider';
import { EditingUsersLoader } from '@view-model/models/sticky/editing-users/EditingUsersLoader';
import { ViewersLoader } from '@view-model/models/sticky/viewers/ViewersLoader';
import { getDefaultStore, useAtomValue } from 'jotai';
import { viewersAtom } from '@view-model/models/sticky/viewers/viewersAtom';
import { throttle } from 'lodash-es';
import type { DragContext } from '@model-framework/ui';
import { StickyNodeAlignIntervalProvider } from '@view-model/models/sticky/StickyNodeView/adapter';
import { viewModelPageContentAtom } from '@user/pages/ViewModelPage/viewModelPageContentAtom';
import { GridArea } from '@view-model/ui/components/Grid/GridArea';
import type { SVGZoomPanHandlerRef } from '@view-model/ui/components/Grid/SVGZoomPanHandler';
import type { ScaleExtent } from '@view-model/ui/components/Grid/domain/ScaleExtent';
import { useDragFileGuard } from '@model-framework/drag-file/useDragFileGuard';
import { NodeCreatableTarget } from '@view-model/ui/components/Model/ModelCanvas/NodeCreatableTarget';
import { dragFileTargetReducer, initialDragFileTarget } from '@model-framework/drag-file/DragFileArea';
import { SVGToImage } from '@model-framework/image/SVGToImage';
import { RightClickMenuOpenStateProvider, useRightClickMenuOpenState } from './useRightClickMenuOpenState';
import { PerfProfiler } from '@framework/utils/PerfProfiler';
import {
  type MCPBridgeAPI,
  MCPBridgeOperationLocator,
} from './FloatingUIContainer/MCPBridge/MCPBridgeOperationLocator';
import { MCPBridgeChatLocator } from './FloatingUIContainer/MCPBridge/MCPBridgeChatLocator';
import { useViewImageCapture } from './ViewImageCapture/useViewImageCapture';
import { type ViewImageCaptureAPI, ViewImageCaptureLocator } from './ViewImageCapture/ViewImageCaptureLocator';

const LINKER_ICON_OFFSET = 160;
const LINKER_ICON_CIRCLE_SIZE = 128;
const FOLLOW_SYNC_ANIMATION_DURATION_MS = 50;

// スケールに応じたビューのタイトルバーの高さを計算（ワールド座標）
// viewTitleBarAtom.ts のロジックを参照
const calcTitleBarHeight = (scale: number): number => {
  const maxScale = 20 / TITLE_BAR_HEIGHT; // ≈ 0.156
  const minScale = 0.07;
  const height = scale <= maxScale ? (1 / Math.max(minScale, scale)) * 20 : TITLE_BAR_HEIGHT;
  const margin = scale <= maxScale ? (1 / Math.max(minScale, scale)) * 2.5 : TITLE_BAR_MARGIN;
  return height + margin * 2;
};

type ViewModelPageContentProps = {
  workspace: WorkspaceEntity;
  viewModel: ViewModelEntity;
  viewModelPermissions: ViewModelPermissions;
  otherUserViewBox: Rect | null;
  setOtherUserClientViewBox: (rect: Rect | null) => void;
};

export const ViewModelPageContent: React.FC<ViewModelPageContentProps> = ({
  workspace,
  viewModel,
  viewModelPermissions,
  otherUserViewBox,
  setOtherUserClientViewBox,
}: ViewModelPageContentProps) => {
  const { t } = useTranslation();
  const currentUserProfile = useCurrentUserPublicProfile();
  const search = useLocation().search;
  const query = useMemo(() => new URLSearchParams(search), [search]);
  const navigate = useNavigate();

  const viewPositionsRepoRef = useRef(new PositionSetRepository(RTDBPath.View.positionsPath(viewModel.id)));
  const selectedViewsPositionSetRef = useRef(PositionSet.buildEmpty());
  const selectedViewsPreviousPositionSetRef = useRef(PositionSet.buildEmpty());

  const setCanvasContainerRef = useProvideContentViewBox();
  const contentViewBox = useContentViewBox();
  const { isSidebarSettled } = useSidebarContext();

  const [contentTransform] = useAtom(contentTransformAtom);
  const actionLogSender = useActionLogSender();
  const commandManagerRef = useCommandManagerRef(viewModel.id, actionLogSender);
  const viewModelOperationLogSender = useViewModelOperationLogSender();
  const operationRef = useRef<ViewModelPageOperation>(null);

  const newElementStyleProviderRef = useRef(new NewElementStyleProvider());

  const [selectedViewIds, setSelectedViewIds] = useState<Set<ViewId>>(new Set());
  const [selectedConsistencyLinkIds, setSelectedConsistencyLinkIds] = useState<Set<ConsistencyLinkId>>(new Set());

  // 表示位置やズームをコントロールする<Frame>コンポーネントへ渡すref
  const svgZoomPanHandlerRef = useRef<SVGZoomPanHandlerRef | null>(null);
  const [scaleExtent, setScaleExtent] = useState<ScaleExtent>({ min: 0.04, max: 1.5 });

  const viewModelSVGRef = useRef<SVGSVGElement>(null);

  const pastOtherUserViewBox = useRef<Rect>(null);
  const windowEventManager = container.get<WindowEventManager>(symbols.WindowEventManager);
  const mouseCursorPubSub = useRef<ClientMouseCursorPubSub>(null);

  const [viewLoaded, setViewLoaded] = useState<boolean>(false);
  const [commentThreadCenterRect, setCommentThreadCenterRect] = useState<Rect | null>(null);
  const [fileContainerCenterRect, setFileContainerCenterRect] = useState<Rect | null>(null);
  const [multiSelectionMode, setMultiSelectionMode] = useState<MultiSelectionMode>(MultiSelectionMode.offMode);
  const [showEmptyMessageModal, setShowEmptyMessageModal] = useState<boolean>(false);
  const [linker, setLinker] = useState<LinkerState<ViewId> | null>(null);

  const [models, setModels] = useState<ModelCollection>(viewModel.getModelCollection());
  const [stickyModelContentsOperations, setStickyModelContentsOperations] = useState<
    Record<ModelId, StickyModelContentsOperation>
  >({});
  const { setActiveRightClickMenuIds } = useRightClickMenuOpenState();

  const [isOpenDeleteViewsConfirmModal, handleOpenDeleteViewsConfirmModal, handleCloseDeleteViewsConfirmModal] =
    useOpenCloseState();

  // visibleAreaの返り値 rectをメモ化している。contentViewBox 未計測時は null
  const rect = useMemo(() => {
    return contentViewBox ? contentViewBox.visibleArea(contentTransform) : null;
  }, [contentViewBox, contentTransform]);

  // ビューモデル画面の閲覧者情報を保存する
  const viewerIconMouseMoveHandler = useWriteViewer(viewModel.id, currentUserProfile, rect);

  // フォロー機能
  const { followingUserId, followingViewer } = useFollowingUser();
  useFollowingViewportSync({ setClientViewBox: setOtherUserClientViewBox });

  // フォロー先ユーザーがビューモデルから離脱した場合に自動でフォロー解除
  useEffect(() => {
    if (followingUserId && !followingViewer) {
      getDefaultStore().set(followingUserIdAtom, null);
      toast(t('viewModel.follow.userLeft'));
    }
  }, [followingUserId, followingViewer, t]);

  const [dragFileTargetState, dispatch] = useReducer(dragFileTargetReducer, initialDragFileTarget);

  const hookDragEnter = (e: DragEvent) => {
    const target = e.target as HTMLElement;
    const viewId = target.id;

    if (!NodeCreatableTarget.isTarget(target)) {
      // Drag先がModelCanvasでない場合
      dispatch({ type: 'Exit', payload: { viewId: '' } });
      return;
    }

    // この呼び出しをトリガーとして、対象となるView上にDnDエリアが表示され、DnDが可能な状態となります
    dispatch({ type: 'Enter', payload: { viewId } });
  };

  const { getGuardProps } = useDragFileGuard({ hookDragEnter });

  useEffect(() => {
    if (!currentUserProfile?.id) return;

    operationRef.current = new ViewModelPageOperation(
      workspace.groupId,
      currentUserProfile.id,
      commandManagerRef.current,
      viewModel.id,
      viewModelOperationLogSender
    );
  }, [viewModel, commandManagerRef, viewModelOperationLogSender, currentUserProfile?.id, workspace.groupId]);

  const { viewContentsRecord } = useAtomValue(viewModelPageContentAtom);
  const viewContents = viewContentsRecord[viewModel.id];
  const viewEntities = viewContents?.getViews().toArray();
  const viewPositionSet = viewContents?.getPositions();
  const isViewsFirstFetched = viewContents !== undefined;

  const [consistencyLinks] = useSnapshot({
    path: RTDBPath.ConsistencyLink.linksPath(viewModel.id),
    load: ({ getChildValues }) => ConsistencyLinkCollection.load(getChildValues() as ConsistencyLinkCollectionJSON),
    onError: () => {
      toast.error(t('common.message.loadFailed'));
    },
  });

  const handleDeleteViews = useHandler(async () => {
    if (!viewEntities) return;

    const selectedViews = viewEntities.filter((v) => selectedViewIds.has(v.id));
    const deletedModelIds: ModelId[] = [];

    const deleteContentsCommands = await Promise.all(
      selectedViews.map(async (v) => {
        const { id, modelId } = v;
        deletedModelIds.push(modelId);
        const DBpathes = [
          RTDBPath.Model.modelPath(viewModel.id, modelId),
          RTDBPath.Model.modelContentPath(viewModel.id, modelId),
          RTDBPath.View.positionPath(viewModel.id, id),
          RTDBPath.View.viewPath(viewModel.id, id),
        ];

        const targetView = viewContents?.findView(id) as ViewEntity;
        if (targetView.meaningPanelViewId) {
          // 削除対象の View が意味の構造パネルを持つ場合はそれも削除する
          const meaningPanel = viewContents?.findView(targetView.meaningPanelViewId);
          if (meaningPanel) {
            deletedModelIds.push(meaningPanel.modelId);
            DBpathes.push(
              RTDBPath.Model.modelPath(viewModel.id, meaningPanel.modelId),
              RTDBPath.Model.modelContentPath(viewModel.id, meaningPanel.modelId),
              RTDBPath.View.viewPath(viewModel.id, meaningPanel.id),
              RTDBPath.View.positionPath(viewModel.id, meaningPanel.id)
            );
          }
        }

        return await CommandHelper.buildDeleteCommandMulti(...DBpathes);
      })
    );

    const targetConsistencyLinks = consistencyLinks?.filterbyViewIds(Array.from(selectedViewIds)) || [];

    const deleteConsistencyLinksCommands = await Promise.all(
      targetConsistencyLinks.map(async (link) => {
        return await CommandHelper.buildDeleteCommand(RTDBPath.ConsistencyLink.linkPath(viewModel.id, link.id));
      })
    );

    const compositeCommand = CompositeCommand.composeOptionalCommands(
      ...deleteContentsCommands.flat(),
      ...deleteConsistencyLinksCommands
    );

    if (compositeCommand) {
      const fileContainers = await Promise.all(
        deletedModelIds.map((modelId) =>
          new RecordRepository(FileContainer, RTDBPath.FileContainer.containersPath(viewModel.id, modelId)).get()
        )
      );
      const assetIds = fileContainers.flatMap((containers) => Object.values(containers).map(({ assetId }) => assetId));
      const deleted = await FileContainerAssetTrashCommand.executeDeletion(
        commandManagerRef.current,
        { groupId: workspace.groupId, viewModelId: viewModel.id },
        assetIds,
        compositeCommand
      );
      if (!deleted) return;
      handleCloseDeleteViewsConfirmModal();

      viewModelOperationLogSender('view:delete', {
        viewId: selectedViews[0].id,
        viewName: selectedViews[0].name.value,
      });
    }
  });

  const handleAddStickyView = useHandler(() => {
    const operation = operationRef.current;
    if (!operation || !contentViewBox) return;

    const { viewId, modelId, modelType } = operation.addStickyView(contentViewBox.visibleArea(contentTransform));
    viewModelOperationLogSender('view:create', { viewId, modelId, modelType });

    setSelectedViewIds(new Set([viewId]));
  });

  const getViewRectOperations = useHandler(
    (): Record<ViewId, { view: ViewEntity; rect?: Rect; operation?: StickyModelContentsOperation }> => {
      if (!viewContents) {
        return {};
      }

      return Object.fromEntries(
        viewContents
          .getViews()
          .toArray()
          .map((view) => {
            return [
              view.id,
              {
                view,
                rect: viewContents.getRectOf(view.id) ?? undefined,
                operation: stickyModelContentsOperations[view.modelId],
              },
            ];
          })
      );
    }
  );

  const viewBoxQuery = useCallback((): URLSearchParams | null => {
    if (!contentViewBox) return null;

    const query = new URLSearchParams();

    const { width, height } = contentViewBox;
    const [x1, y1] = contentTransform.invert([-width / 2, -height / 2]); // 左上
    const [x2, y2] = contentTransform.invert([width / 2, height / 2]); // 右下

    query.set('x1', String(Math.round(x1)));
    query.set('x2', String(Math.round(x2)));
    query.set('y1', String(Math.round(y1)));
    query.set('y2', String(Math.round(y2)));

    return query;
  }, [contentTransform, contentViewBox]);

  const handleGenerateLink = useHandler(() => {
    const query = viewBoxQuery();
    if (!query) return;

    const url = `${window.location.origin}${window.location.pathname}?${query.toString()}`;
    // クリップボードにコピー
    navigator.clipboard
      .writeText(url)
      .then(() => {
        toast(t('common.message.copySuccess'));
      })
      .catch(() => {
        toast.error(t('common.message.copyFailed'));
      });
  });

  const handleOnClickViewModelLink = useHandler(() => {
    const query = viewBoxQuery();
    if (!query) return;
    navigate({ search: query.toString() }, { replace: true });
  });

  const handlePasteViews = useCallback(
    async (clipboard: ViewsClipboardPayload) => {
      const operation = operationRef.current;
      if (!operation || !contentViewBox) return;

      const [pastedViewIds, errorMessage] = await operation.pasteViews(
        clipboard,
        contentViewBox.visibleArea(contentTransform).getCenterPoint()
      );

      if (errorMessage) {
        toast.error(errorMessage);
      } else {
        toast.success(t('viewModel.view.pasteSuccess'));
      }

      setSelectedViewIds(pastedViewIds);
    },
    [contentTransform, contentViewBox, t]
  );

  const handleZoomIn = useHandler((): void => {
    svgZoomPanHandlerRef.current?.zoom(1.25);
  });

  const handleZoomOut = useHandler((): void => {
    svgZoomPanHandlerRef.current?.zoom(0.8);
  });

  const handleZoomReset = useHandler((): void => {
    if (!viewContents) return;

    const bounds = viewContents.getBounds();
    if (!bounds) return;

    const params = calcZoomToFitParams(bounds);
    if (!params) return;
    svgZoomPanHandlerRef.current?.resetZoom(params.centerPosition, params.scale);
  });

  const onMousePointerDetected = useMemo(
    () =>
      throttle(async (x: number, y: number) => {
        recordCalibrationEvent('cursor.sample');
        const selfClientId = getClientId();
        const otherViewers = getDefaultStore()
          .get(viewersAtom)
          .filter((viewer) => viewer.clientId !== selfClientId);

        // 自分以外がいる場合のみマウスカーソル位置を永続化(同じユーザーでも別タブで開いている場合には永続化)
        if (otherViewers.length > 0) {
          recordCalibrationEvent('cursor.publish');
          await mouseCursorPubSub.current?.publish(x, y);
        }

        // マウスカーソルの位置移動がある間は、閲覧者アイコンを保持する
        viewerIconMouseMoveHandler();
      }, 100),
    [viewerIconMouseMoveHandler]
  );

  useEffect(() => {
    // 未ログイン状態であれば、ModelContentsのリスンは行わない
    if (!currentUserProfile) return;

    const modelRepository = new ModelRepository(viewModel.id);
    modelRepository.addListener(
      (model) => {
        setModels(viewModel.addModel(model));

        if (!(model instanceof StickyModel)) return;

        const nodeRepository = new NodeRepository(viewModel.id, model.id);
        const linkRepository = new LinkRepository(viewModel.id, model.id);
        const zoneRepository = new StickyZoneRepository(viewModel.id, model.id);

        const displayOrderRepository = new DisplayOrderRepository(viewModel.id, model.id);
        const nodeEntityOperation = new NodeEntityOperation(nodeRepository);
        const linkEntityOperation = new LinkEntityOperation(linkRepository);
        const zoneEntityOperation = new StickyZoneEntityOperation(model, zoneRepository);

        const positionMapRepository = new StickyModelElementPositionMapRepository(viewModel.id, model.id);

        const operation = new StickyModelContentsOperation(
          workspace.groupId,
          viewModel,
          model,
          currentUserProfile.id,
          nodeRepository,
          linkRepository,
          zoneRepository,
          commandManagerRef.current,
          nodeEntityOperation,
          linkEntityOperation,
          zoneEntityOperation,
          positionMapRepository,
          displayOrderRepository,
          newElementStyleProviderRef.current
        );

        setStickyModelContentsOperations((state) => {
          return { ...state, [model.id]: operation };
        });
      },
      (model) => setModels(viewModel.updateModel(model)),
      (model) => {
        setModels(viewModel.removeModel(model.key));
        setStickyModelContentsOperations((state) => {
          const { [model.id]: _removed, ...newState } = state;
          return newState;
        });
      }
    );
    return () => {
      modelRepository.removeListener();
      setModels(viewModel.resetModels());
      setStickyModelContentsOperations({});
    };
  }, [currentUserProfile, commandManagerRef, newElementStyleProviderRef, viewModel, workspace.groupId]);

  useEffect(() => {
    if (!currentUserProfile) return;

    const pubSub = new ClientMouseCursorPubSub(getClientId(), currentUserProfile, viewModel.id);
    mouseCursorPubSub.current = pubSub;

    return () => pubSub.unsubscribe();
  }, [currentUserProfile, viewModel.id]);

  useViewModelPageContentShortcuts({
    commandManagerRef,
    viewModel,
    selectedViewIds,
    selectedConsistencyLinkIds,
  });

  useEffect(() => {
    windowEventManager.onMounted((windowActive: boolean) => {
      // ウィンドウがアクティブでなくなった場合には、 ShiftKey 押下中状態を解除する
      if (!windowActive) {
        setMultiSelectionMode(MultiSelectionMode.offMode);
      }
    });
    return () => windowEventManager.onWillUnmount();
  }, [windowEventManager]);

  useEffect(() => {
    const unsubscribe = windowEventManager.listenOnPaste(
      async (payload: ApplicationClipboardPayload): Promise<void> => {
        // 編集できない場合には、貼り付け処理を無視する
        if (!viewModelPermissions.isContentEditable) return;
        if (!payload) return;

        if (isViewsClipboardPayload(payload)) {
          handlePasteViews(payload);
        }
      }
    );
    return () => unsubscribe();
  }, [handlePasteViews, viewModelPermissions.isContentEditable, windowEventManager]);

  useEffect(() => {
    const unsubscribe = windowEventManager.listenOnCopy(async (): Promise<void> => {
      if (!viewContents) return;
      if (selectedViewIds.size <= 0) return;

      if (Object.values(stickyModelContentsOperations).some((o) => o.hasSelectedElements())) return;

      const rawSelectedViews = viewContents.getViewsByIds(Array.from(selectedViewIds));
      // 選択されているビューのうち、originalのみをコピーする(meaningPanelは除外)
      let selectedViews = rawSelectedViews.filterByViewType('original');
      if (selectedViews.isEmpty()) return;

      // meaningPanel を持つビューが選択されていればその meaningPanel もコピーする
      const meaningPanelViewIds: ViewId[] = selectedViews
        .toArray()
        .map((view) => view.meaningPanelViewId)
        .filter((id) => !!id) as ViewId[];
      const meaningPanelViews = viewContents?.getViews().filterByIds(meaningPanelViewIds);
      selectedViews = selectedViews.addViews(meaningPanelViews.toArray());

      const selectedViewPositions = viewContents.getPositionsByIds(selectedViews.allIds());

      const success = await ViewsClipboardOperator.copy(
        viewModel.id,
        selectedViews,
        selectedViewPositions,
        consistencyLinks,
        viewModelOperationLogSender
      );

      if (success) {
        toast.success(t('common.message.copySuccess'));
      } else {
        toast.error(t('common.message.copyFailed'));
      }
    });

    return () => unsubscribe();
  }, [
    viewModelOperationLogSender,
    windowEventManager,
    selectedViewIds,
    viewContents,
    viewModel.id,
    consistencyLinks,
    stickyModelContentsOperations,
    t,
  ]);

  // ┌─────────────────────────────┐
  // │ ヘッダー 100px               │
  // ├─────────────────────────────┤
  // │左│                        │右│
  // │70│   この領域の中央 に      │20│
  // │px│   boundsの中心が来る    │px│
  // │  │                       │  │
  // ├─────────────────────────────┤
  // │ フッター 70px                │
  // └─────────────────────────────┘
  // boundsがUIに隠れない領域に収まるズームパラメータを計算
  // WARN: 引数の bounds にタイトルバーを含まないこと
  // 引数の bounds はタイトルバー分を含まない前提で計算をしているため、含んだ Rect を渡す場合計算がズレることが想定される
  const calcZoomToFitParams = useCallback(
    (bounds: Rect): { scale: number; centerPosition: Point } | null => {
      // contentViewBox 未計測時にはズームパラメータを計算できない
      if (!contentViewBox) return null;

      // スクリーン座標でのUI余白（固定）
      const UI_HEADER_HEIGHT = 100;
      const UI_FOOTER_HEIGHT = 70;
      const UI_LEFT_MARGIN = 70; // ツールバー分
      const UI_RIGHT_MARGIN = 20; // 右側の余裕

      const UI_FALLBACK_EFFECTIVE_HEIGHT = 100;
      const UI_FALLBACK_EFFECTIVE_WIDTH = 100;

      // 有効表示領域（UIを除いた領域、最小100px）
      const effectiveWidth = Math.max(
        contentViewBox.width - UI_LEFT_MARGIN - UI_RIGHT_MARGIN,
        UI_FALLBACK_EFFECTIVE_WIDTH
      );
      const effectiveHeight = Math.max(
        contentViewBox.height - UI_HEADER_HEIGHT - UI_FOOTER_HEIGHT,
        UI_FALLBACK_EFFECTIVE_HEIGHT
      );
      const effectiveViewBox = new Size(effectiveWidth, effectiveHeight);

      // 【なぜ反復計算が必要か】
      // タイトルバー高さはスケールに依存し（ズームアウト時は可視性維持のため大きくなる）、
      // スケールはboundsに依存し、boundsはタイトルバー高さに依存する（循環依存）
      // この循環を解決するため、収束するまで反復計算を行う（通常2回で収束）
      const adjustedBounds = ((): Rect => {
        let result = bounds;
        const { left, top, right, bottom } = bounds.getLTRB();
        // この計算の収束性について
        // see: https://github.com/levii/balus-app/pull/7659#discussion_r2692855654
        for (let i = 0; i < 3; i++) {
          const scale = effectiveViewBox.calcRatio(result.getSize());
          const titleBarHeight = calcTitleBarHeight(scale);
          const newBounds = Rect.fromLTRB(left, top - titleBarHeight, right, bottom);
          if (result.isEqual(newBounds)) break;
          result = newBounds;
        }
        return result;
      })();

      const scale = effectiveViewBox.calcRatio(adjustedBounds.getSize());

      // 中心をオフセット（左右・上下の差分だけずらす）
      const centerOffsetX = (UI_LEFT_MARGIN - UI_RIGHT_MARGIN) / 2 / scale;
      const centerOffsetY = (UI_HEADER_HEIGHT - UI_FOOTER_HEIGHT) / 2 / scale;
      const centerPosition = new Point(
        adjustedBounds.getCenterPoint().x - centerOffsetX,
        adjustedBounds.getCenterPoint().y - centerOffsetY
      );
      return { scale, centerPosition };
    },
    [contentViewBox]
  );

  const getVisibleZone = useCallback((): Rect => {
    // クエリパラメータで表示範囲が数値で正しく指定されていれば、その範囲を返す
    const [x1, y1] = [Number(query.get('x1')), Number(query.get('y1'))];
    const [x2, y2] = [Number(query.get('x2')), Number(query.get('y2'))];
    if (x1 && y1 && x2 && y2) {
      return Rect.fromLTRB(x1, y1, x2, y2);
    }

    // コメントスレッドの位置が取得済みであれば、そのスレッドが中心に表示されるように範囲を返す
    if (commentThreadCenterRect) {
      return commentThreadCenterRect;
    }

    if (fileContainerCenterRect) {
      return fileContainerCenterRect;
    }

    const viewId = query.get('targetView');
    if (viewId) {
      const targetView = viewContents?.findView(viewId);
      if (targetView) setSelectedViewIds(new Set([targetView.id]));
      const viewRect = viewContents?.getRectOf(viewId);
      if (viewRect) return viewRect;
    }

    // ビューが存在するならば、それらのビューが全て表示されるように範囲を返す
    const bounds = viewContents?.getBounds();
    if (bounds) {
      return bounds;
    }

    // Viewが0個の場合のデフォルト表示範囲
    const DEFAULT_VISIBLE_ZONE = Rect.fromSize(512 * 7, 512 * 5);
    return DEFAULT_VISIBLE_ZONE;
  }, [query, commentThreadCenterRect, fileContainerCenterRect, viewContents, setSelectedViewIds]);

  // ビューモデル内遷移でクエリパラメータが変わった場合は表示位置調整が走るようにviewLoadedフラグをオフにする。
  // フラグの名前は見直した方がいいかもしれない。
  useEffect(() => {
    setViewLoaded(false);
  }, [query]);

  // targetCommentThread が URL に含まれる場合、スレッドの位置を RTDB から非同期で取得する。
  // コメント位置はビューローカル座標のため、ビュー位置を加算してキャンバス絶対座標に変換する。
  // 取得後に commentThreadCenterRect を更新し viewLoaded をリセットすることで、
  // 続くズーム調整 effect がスレッドを中心に表示する。
  useEffect(() => {
    const threadId = query.get('targetCommentThread');
    const modelId = query.get('targetModel');
    if (!threadId || !modelId || !viewContents) {
      setCommentThreadCenterRect(null);
      return;
    }
    // 非同期取得が完了するまでの間、古い Rect が可視範囲計算に混ざらないよう先にクリアする
    setCommentThreadCenterRect(null);

    // modelId からビューを特定してビューのキャンバス上の位置を取得する
    const containingView = viewContents
      .getViews()
      .toArray()
      .find((v) => v.modelId === modelId);
    const viewPosition = containingView ? viewContents.findPosition(containingView.id) : undefined;

    let cancelled = false;
    const repo = new PositionSetRepository(RTDBPath.Comment.positionsPath(viewModel.id, modelId));
    repo
      .getMany([threadId])
      .then((positionSet) => {
        if (cancelled) return;
        const point = positionSet.find(threadId);
        if (!point) {
          setCommentThreadCenterRect(null);
          return;
        }
        // コメント位置（ビューローカル）にビュー位置を加算してキャンバス絶対座標を算出する
        const canvasX = (viewPosition?.x ?? 0) + point.x;
        const canvasY = (viewPosition?.y ?? 0) + point.y;
        // アイコン左に余白を確保し、スレッドパネル全体（アイコン＋ギャップ＋パネル幅）が収まる Rect を構築する
        const PADDING = 200;
        const THREAD_TOTAL_WIDTH = 1200; // アイコン幅 + gap(32) + パネル幅(740) + 余白
        const THREAD_TOTAL_HEIGHT = 800;
        const rect = Rect.fromLTRB(
          canvasX - PADDING,
          canvasY - PADDING,
          canvasX + THREAD_TOTAL_WIDTH,
          canvasY + THREAD_TOTAL_HEIGHT
        );
        setCommentThreadCenterRect(rect);
        setViewLoaded(false);
      })
      .catch((e) => {
        console.warn(e);
        // 取得失敗時も null に戻してフォールバック（targetView 等）が効く状態にする
        if (!cancelled) setCommentThreadCenterRect(null);
      });
    return () => {
      cancelled = true;
    };
  }, [query, viewModel.id, viewContents]);

  // targetFileContainer が URL に含まれる場合、ファイルコンテナを中心に表示して選択する。
  // 取得は URL ごとに 1 回だけ行い、URL が変われば取得中の結果と表示範囲を捨てる。
  // 反映はビューの位置と選択用の操作オブジェクト（モデルの読み込み後に非同期で揃う）を待ってから 1 回だけ行い、
  // 反映後にビューの移動などで viewContents が変わっても選択し直さない。
  // 表示範囲をコメント用と分けているのは、コメント用の effect が viewContents の更新のたびに範囲を消すため。
  // 共有すると、ズームの待機中にビューが更新されたときにファイルの範囲が失われる
  const [fileContainerJumpTarget, setFileContainerJumpTarget] = useState<{
    modelId: ModelId;
    fileContainer: FileContainer;
    position: Point;
  } | null>(null);
  useEffect(() => {
    setFileContainerJumpTarget(null);
    setFileContainerCenterRect(null);
    const fileContainerId = query.get('targetFileContainer');
    const modelId = query.get('targetModel');
    if (!fileContainerId || !modelId) return;

    let cancelled = false;
    Promise.all([
      new ObjectRepository(
        FileContainer,
        RTDBPath.FileContainer.containerPath(viewModel.id, modelId, fileContainerId)
      ).get(),
      new PositionSetRepository(RTDBPath.FileContainer.positionsPath(viewModel.id, modelId)).getMany([fileContainerId]),
    ])
      .then(([fileContainer, positionSet]) => {
        const position = positionSet.find(fileContainerId);
        if (cancelled || !fileContainer || !position) return;
        setFileContainerJumpTarget({ modelId, fileContainer, position });
      })
      .catch((e) => console.warn(e));
    return () => {
      cancelled = true;
    };
  }, [query, viewModel.id]);

  useEffect(() => {
    if (!fileContainerJumpTarget || !viewContents) return;
    const { modelId, fileContainer, position } = fileContainerJumpTarget;
    const containingView = viewContents
      .getViews()
      .toArray()
      .find((v) => v.modelId === modelId);
    const viewPosition = containingView && viewContents.findPosition(containingView.id);
    const operation = stickyModelContentsOperations[modelId];
    if (!containingView || !viewPosition || !operation) return;

    // 閉じている意味の構造パネル内のファイルへは、移動してもカードが描画されない（コメント一覧からの移動と同じ）。
    // パネルの開閉は全員で共有される状態で閲覧のみのユーザーは変更できず、自分の画面だけで開く仕組みも無いため受容している
    const FILE_CONTAINER_MARGIN = 400;
    // 同じコミットで走る初回フィット（後続の effect）が viewLoaded を true にしてズームを上書きすることと、
    // 同じコミットで初めてマウントされたモデルの effect の cleanup（StrictMode の再実行）が選択を消すことを避けるため、
    // コミットの後に反映する
    const timer = window.setTimeout(() => {
      setFileContainerCenterRect(
        fileContainer.getRect(viewPosition.add(position)).applyMarginKeepingCenter(FILE_CONTAINER_MARGIN)
      );
      setViewLoaded(false);
      operation.selectOnly(fileContainer.id);
      setSelectedViewIds(new Set([containingView.id]));
      setSelectedConsistencyLinkIds(new Set());
      setMultiSelectionMode(MultiSelectionMode.offMode);
      setFileContainerJumpTarget(null);
    });
    return () => window.clearTimeout(timer);
  }, [fileContainerJumpTarget, viewContents, stickyModelContentsOperations, setSelectedViewIds]);

  useEffect(() => {
    if (!viewContents) return;

    // 初回未取得ならばスキップ
    if (!isViewsFirstFetched) return;

    // 初回描画済みならば、表示範囲に合わせての自動リサイズをスキップ
    if (viewLoaded) return;

    // キャンバスサイズが未計測ならスキップ。計測されると contentViewBox
    //（calcZoomToFitParams の依存）が変わるので、この effect が再実行される
    if (!contentViewBox) return;

    // サイドバー利用可否が確定するまでスキップ。確定前はサイドバー非表示のフル幅で
    // 計測されており、そのままフィットすると確定後の幅とズレる
    if (!isSidebarSettled) return;

    // 既存のビュー全てが表示範囲に含まれるように適切にリサイズする
    // ビューが 0 個の時には、標準サイズのビューが良い感じに収まるように調整しておく
    const visibleZone = getVisibleZone();
    const params = calcZoomToFitParams(visibleZone);
    if (!params) return;
    const { scale, centerPosition } = params;
    svgZoomPanHandlerRef.current?.resetZoom(centerPosition, scale);

    // 初期の scaleExtent よりも縮小表示が必要な場合には scaleExtent を再設定する
    if (scale < scaleExtent.min) {
      setScaleExtent({ min: scale * 0.8, max: scaleExtent.max });
    }
    if (!viewContents.isEmpty()) {
      setViewLoaded(true);
      setShowEmptyMessageModal(false);
      return;
    }

    // viewContentsがまだ読み込まれていない時と何もない時を区別
    // viewContentsが何もないことが確定した時にのみEmptyMessageModalが表示されるようにする
    setShowEmptyMessageModal(true);
  }, [
    viewModel,
    contentViewBox,
    isSidebarSettled,
    scaleExtent,
    viewLoaded,
    getVisibleZone,
    calcZoomToFitParams,
    setScaleExtent,
    viewContents,
    isViewsFirstFetched,
  ]);

  // otherUserViewBoxにセットされた他の閲覧中ユーザーの閲覧範囲へ移動する
  useEffect(() => {
    if (!otherUserViewBox || !contentViewBox || pastOtherUserViewBox.current == otherUserViewBox) return;
    const viewBoxRect = Rect.fromSize(contentViewBox.width, contentViewBox.height);
    const scale = viewBoxRect.getSize().calcRatio(otherUserViewBox.getSize());
    const centerPosition = otherUserViewBox.getCenterPoint();

    // フォロー中はスムーズなアニメーションで同期、それ以外は即座に移動
    const duration = followingUserId ? FOLLOW_SYNC_ANIMATION_DURATION_MS : undefined;
    svgZoomPanHandlerRef.current?.resetZoom(centerPosition, scale, duration);

    // 初期の scaleExtent よりも縮小表示が必要な場合には scaleExtent を再設定する
    if (scale < scaleExtent.min) {
      setScaleExtent({ min: scale * 0.8, max: scaleExtent.max });
    }
    // フォロー中は継続的に同期するのでトーストを表示しない
    if (!followingUserId) {
      toast(t('viewModel.follow.movedToUser'));
    }
    pastOtherUserViewBox.current = otherUserViewBox;
  }, [otherUserViewBox, scaleExtent, contentViewBox, viewModel, setScaleExtent, followingUserId, t]);

  const focusRect = useHandler((rect: Rect) => {
    if (!contentViewBox) return;

    const targetRect = rect.applyMarginKeepingCenter(512);
    const viewBoxRect = Rect.fromSize(contentViewBox.width, contentViewBox.height);
    const scale = viewBoxRect.getSize().calcRatio(targetRect.getSize());
    const centerPosition = targetRect.getCenterPoint();

    svgZoomPanHandlerRef.current?.resetZoom(centerPosition, scale);
  });

  const focusView = useHandler((viewId: string) => {
    const viewRect = viewContents?.getRectOf(viewId);
    if (!viewRect) return;

    focusRect(viewRect);
  });

  const handleHuddleMessage = useCallback(
    (message: HuddleMessage) => {
      setActiveRightClickMenuIds([]); // 右クリックメニューが開いている場合は閉じる
      focusView(message.viewId);
      if (!message.isOwnMessage(currentUserProfile?.id)) {
        toast(t('viewModel.huddle.assembled', { callerName: message.getCallerName() }));
      }
    },
    [currentUserProfile, focusView, setActiveRightClickMenuIds, t]
  );

  // ビューへの集合通知購読
  useEffect(() => {
    const channels = new ViewModelNotification(viewModel.id);
    const channel = channels.huddleChannel();

    channel.subscribe(handleHuddleMessage);

    return () => channel.unsubscribe();
  }, [handleHuddleMessage, viewModel.id]);

  const handleSelectConsistencyLink = useCallback(
    (id: string | null) => {
      setSelectedConsistencyLinkIds(id ? new Set([id]) : new Set());
      setSelectedViewIds(new Set());
    },
    [setSelectedViewIds]
  );

  const handleSelectSingleView = useCallback(
    (viewId: ViewId): void => {
      setSelectedViewIds(new Set([viewId]));
      setSelectedConsistencyLinkIds(new Set());
    },
    [setSelectedViewIds]
  );

  const handleToggleSelectedViews = useCallback((viewId: ViewId) => {
    setSelectedViewIds((prev) => {
      const newSelectedViewIds = new Set(prev);
      if (newSelectedViewIds.has(viewId)) {
        newSelectedViewIds.delete(viewId);
      } else {
        newSelectedViewIds.add(viewId);
      }

      return newSelectedViewIds;
    });
  }, []);

  const handleDeselectAllViews = useCallback(() => {
    setSelectedViewIds(new Set());
  }, []);

  // 画像キャプチャ操作（返される関数は useHandler により同一性が安定している）
  const { captureViewModelImage, captureViewImage } = useViewImageCapture({
    viewModelSVGRef,
    viewContents,
    deselectAllViews: handleDeselectAllViews,
    zoomToFit: handleZoomReset,
  });

  useEffect(() => {
    const api: ViewImageCaptureAPI = { captureViewModelImage, captureViewImage };
    ViewImageCaptureLocator.register(api);
    return () => ViewImageCaptureLocator.unregister(api);
  }, [captureViewModelImage, captureViewImage]);

  // 依存配列は operationRef.current を生成する手前の useEffect と揃える。
  // register する api は将来 operationRef.current のメソッドを呼ぶため、
  // operationRef.current が再生成されるたびに register もやり直す必要がある。
  useEffect(() => {
    if (!operationRef.current || !currentUserProfile?.id) return;
    const api: MCPBridgeAPI = {
      requestModeling: (_viewModelId, instruction) => {
        const runner = MCPBridgeChatLocator.get();
        return runner(instruction);
      },
    };
    MCPBridgeOperationLocator.register(api);
    return () => MCPBridgeOperationLocator.unregister(api);
  }, [viewModel, commandManagerRef, viewModelOperationLogSender, currentUserProfile?.id, workspace.groupId]);

  const deselectElements = useCallback((): void => {
    setSelectedViewIds(new Set());
    setSelectedConsistencyLinkIds(new Set());
    setMultiSelectionMode(MultiSelectionMode.offMode);
  }, [setSelectedViewIds]);

  const orderedViews = viewContents?.getViews().bringSelectedToFront(selectedViewIds);

  const handleLinkerStart = useCallback(
    (startPosition: Position): void => {
      if (selectedViewIds.size !== 1) return;
      const selectedViewId = Array.from(selectedViewIds)[0];

      setLinker(LinkerState.creatingStart(selectedViewId, startPosition));
    },
    [selectedViewIds]
  );

  const handleLinkerMove = useCallback(
    (currentPosition: Position): void => {
      if (!viewContents || !linker || !linker.source) {
        return;
      }

      // currentPositionはLinkCreatorの属する要素（整合性リンク作成開始したビュー）の左上座標からの相対座標なので
      // それを補正した上で対象のビューを探す
      const sourcePosition = viewContents.findPosition(linker.source);
      if (!sourcePosition) return;

      const linkerPosition = Point.fromPosition(currentPosition).addXY(sourcePosition.x, sourcePosition.y);

      const foundView = viewContents.findForegroundViewByPosition(linkerPosition);
      setLinker(linker.move(foundView?.id || null, currentPosition));
    },
    [linker, viewContents]
  );

  const handleLinkerEnd = useCallback((): void => {
    if (!viewContents || !linker) {
      return;
    }
    const sourceViewId = linker.source;
    const targetViewId = linker.target;
    setLinker(null);

    if (!sourceViewId || !targetViewId) {
      return;
    }
    if (sourceViewId === targetViewId) {
      return;
    }
    const sourceView = viewContents.findView(sourceViewId);
    const targetView = viewContents.findView(targetViewId);
    if (!sourceView || !targetView) {
      return;
    }

    if (sourceView.viewType === 'meaningPanel' || targetView.viewType === 'meaningPanel') {
      return;
    }

    ConsistencyLinksOperation.handleCreate({
      fromViewKey: sourceView.key,
      toViewKey: targetView.key,
      commandManager: commandManagerRef.current,
      viewModelId: viewModel.id,
    });
  }, [linker, viewContents, commandManagerRef, viewModel.id]);

  const handleDragViewsStart = useHandler((viewId: ViewId) => {
    if (!viewPositionSet) {
      return;
    }

    // 選択されているビューがある状態でも別の未選択のビューを移動することを可能にするために
    // ドラッグ開始時にどのビューのタイトルバーが押下されたかに応じて更新するpositionSetを決める
    // 選択されているビューのうちの１つのタイトルバーを掴む→選択されているビュー全体を移動
    // 選択されていないビューのタイトルバーを掴む→そのビューのみを移動
    const selectedViewIdArray = selectedViewIds.has(viewId) ? Array.from(selectedViewIds) : [viewId];
    // 選択されているビューが意味の構造パネルを持っている場合はそのパネルも移動対象に追加
    const meaningPanelViewId = orderedViews?.findById(viewId)?.meaningPanelViewId;
    if (meaningPanelViewId) {
      selectedViewIdArray.push(meaningPanelViewId);
    }

    const targetPositionSet = viewPositionSet.getMulti(selectedViewIdArray);

    selectedViewsPositionSetRef.current = targetPositionSet;
    selectedViewsPreviousPositionSetRef.current = targetPositionSet;
  });

  const handleDragViews = useHandler((context: DragContext) => {
    const repo = viewPositionsRepoRef.current;
    const selectedViewsPositionSet = selectedViewsPreviousPositionSetRef.current;
    const movedPositionSet = selectedViewsPositionSet.moveAll(
      context.x - context.dragStartX,
      context.y - context.dragStartY
    );

    repo.saveMany(movedPositionSet).then();
    selectedViewsPositionSetRef.current = movedPositionSet;
  });

  const handleDragViewsEnd = useHandler(() => {
    const repo = viewPositionsRepoRef.current;
    const previousPositionSet = selectedViewsPreviousPositionSetRef.current;
    const selectedViewsPositionSet = selectedViewsPositionSetRef.current;

    const commandManager = commandManagerRef.current;

    const snappedPositionSet = selectedViewsPositionSet.snapToLayout(viewGridLayout);

    // コマンド実行（previousと動かしてるやつ）
    const command = new PositionSetUpdateCommand(previousPositionSet, snappedPositionSet, repo);

    if (previousPositionSet.isEqual(snappedPositionSet)) {
      command.do();
    } else {
      commandManager.execute(command);
    }

    // positionSetRefを初期化
    selectedViewsPositionSetRef.current = PositionSet.buildEmpty();
    selectedViewsPreviousPositionSetRef.current = PositionSet.buildEmpty();
  });

  const handleRectSelection = useHandler(() => {
    setMultiSelectionMode(MultiSelectionMode.multiViewsSelectionMode);
  });

  const handleRectSelectionEnd = useHandler((selectionRect: Rect) => {
    // selectionRectを元に複数ビューを取得
    const filteredViewContents = viewContents?.filterByRect(selectionRect)?.filterByViewType('original');
    if (filteredViewContents && !filteredViewContents.isEmpty()) {
      // Viewが選択されている時は、View間リンクは選択しない
      const filteredViewIds = filteredViewContents.getViews().map((view) => view.id);
      const newSelectedViewIds = new Set([...selectedViewIds, ...filteredViewIds]);
      setSelectedViewIds(newSelectedViewIds);
      setSelectedConsistencyLinkIds(new Set());

      // 選択結果のビュー数に応じたモードを設定
      if (newSelectedViewIds.size >= 2) {
        setMultiSelectionMode(MultiSelectionMode.multiViewsSelectionMode);
      } else if (newSelectedViewIds.size === 1) {
        setMultiSelectionMode(MultiSelectionMode.readyForMultiSelectionMode);
      } else {
        setMultiSelectionMode(MultiSelectionMode.offMode);
      }
    } else if (consistencyLinks && viewContents) {
      const selectedIds = ConsistencyLinksOperation.filterByRect(consistencyLinks, viewContents, selectionRect);
      setSelectedConsistencyLinkIds((prev) => {
        return new Set([...prev, ...selectedIds]);
      });
      setMultiSelectionMode(MultiSelectionMode.offMode);
    }
  });

  // Zoneから新しくViewを作成する
  const handleZoneToViewSelected = useHandler((sourceView: ViewEntity, operation: StickyModelContentsOperation) => {
    const viewModelPageOperation = operationRef.current;
    if (!viewModelPageOperation) return;

    const { topZone, topZonePosition } = operation.getSelectedTopZone();
    if (!topZone || !topZonePosition) {
      return;
    }

    // 選択中の要素のうち外側のZoneを省いたコンテンツ（各要素はZoneからの位置に補正）
    const contents = operation.buildSelectedContentsWithoutTopZone(topZone.id);
    const clonedContents = contents.cloneNew({}).move(-topZonePosition.x, -topZonePosition.y);

    // タイトルはZoneのコンテンツから
    const title = topZone.text.value;
    const viewSize = topZone.size.getClosestViewSize();

    const sourceViewPosition = viewPositionSet?.find(sourceView.id) || new Point(0, 0);

    // Zoneの右斜め下で一番近いグリッド位置
    const x = calcClosest(sourceViewPosition.x + topZonePosition.x, VIEW_GRID_SIZE);
    const y = calcClosest(sourceViewPosition.y + topZonePosition.y, VIEW_GRID_SIZE);

    const rect = new Rect({ x, y }, { width: viewSize.width, height: viewSize.height });

    const { viewId, modelId, modelType } = viewModelPageOperation.convertZoneToView(rect, clonedContents, title);
    viewModelOperationLogSender('view:create', { viewId, modelId, modelType });

    setSelectedViewIds(new Set([viewId]));
  });

  const calcClosest = (value: number, unitValue: number) => {
    const remainder = value % unitValue;
    if (value >= 0 || remainder === 0) {
      return value - remainder + unitValue;
    } else {
      // 負数かつ剰余あり
      return value - remainder;
    }
  };

  const handleExportViewModelAsImage = useHandler(async (isWhole: boolean) => {
    if (!viewModelSVGRef.current) {
      // eslint-disable-next-line no-console
      console.log('[handleExportViewModelAsImage] viewModelSVGRef is null');
      return;
    }

    handleDeselectAllViews();

    if (isWhole) {
      // 全体をイメージとして出力するため、zoom to fitを実行
      handleZoomReset();
    }

    toast(t('common.message.imageExportStarted'));

    if (await SVGToImage.export(viewModelSVGRef.current, `${viewModel.name}.png`)) {
      toast.success(t('common.message.imageExportCompleted'));
    } else {
      toast.error(t('common.message.imageExportFailed'));
    }
  });

  const handleCopyView = useCallback(
    async (viewId: ViewId) => {
      const targetView = viewContents?.getViews().findById(viewId);
      if (!targetView || !viewContents) return;

      let targetViews = new ViewCollection([targetView]);

      // Copy対象の View が意味の構造パネルを持つ場合はそれもコピー対象に追加する
      if (targetView.meaningPanelViewId) {
        const meaningPanelView = viewContents?.getViews().findById(targetView.meaningPanelViewId);
        if (meaningPanelView) {
          targetViews = targetViews.addViews([meaningPanelView]);
        }
      }
      const selectedViewPositions = viewContents.getPositionsByIds(targetViews.allIds());
      const targetViewPositions: { [key: ViewId]: Point } = {};
      targetViews.allIds().map((id) => {
        targetViewPositions[id] = selectedViewPositions.find(id) as Point;
      });

      const success = await ViewsClipboardOperator.copy(
        viewModel.id,
        targetViews,
        selectedViewPositions,
        null,
        viewModelOperationLogSender
      );

      if (success) {
        toast.success(t('common.message.copySuccess'));
      } else {
        toast.error(t('common.message.copyFailed'));
      }
    },
    [viewContents, viewModel.id, viewModelOperationLogSender, t]
  );

  const {
    creatingComments,
    handleCreatingCommentAdd,
    handleCreatingCommentDrag,
    handleCreatingCommentDragEnd,
    handleCreatingCommentCancel,
    handleCreatingCommentSubmit,
  } = useCreatingComments(stickyModelContentsOperations);

  // 整合性リンク作成アイコンの表示位置のための変数
  const selectedViewPosition =
    (selectedViewIds.size === 1 && viewContents?.findPosition(Array.from(selectedViewIds)[0])) || null;
  const selectedViewSize =
    (selectedViewIds.size === 1 && viewContents?.findView(Array.from(selectedViewIds)[0])?.size) || null;
  // 選択された View がロックされているかどうか
  const selectedViewIsLocked =
    selectedViewIds.size === 1 && viewContents?.findView(Array.from(selectedViewIds)[0])?.setting.isLocked;
  // 選択された View が意味の構造パネルであるかどうか
  const selectedViewIsMeaningPanel =
    selectedViewIds.size === 1 && viewContents?.findView(Array.from(selectedViewIds)[0])?.viewType === 'meaningPanel';

  return (
    <Fragment>
      <ViewModelSVGRefContextProvider value={viewModelSVGRef}>
        <VisibleAreaCenterPointProvider visibleAreaCenterPoint={rect?.getCenterPoint() ?? new Point(0, 0)}>
          <RightClickMenuOpenStateProvider>
            <CommandManagerContext.Provider value={commandManagerRef.current}>
              <NewElementStyleProviderContext.Provider value={newElementStyleProviderRef.current}>
                <StickyNodeAlignIntervalProvider>
                  {/* ビューモデル全体に対するペースト操作をハンドリングするコンポーネント */}
                  {viewModelPermissions.isContentEditable && (
                    <ViewModelPaste
                      groupId={workspace.groupId}
                      viewModelId={viewModel.id}
                      onSelectViewIds={setSelectedViewIds}
                    />
                  )}

                  <div className="flex h-screen flex-row">
                    {commandManagerRef.current && (
                      <AIAssistantOperationProvider
                        viewModelPageOperationRef={operationRef}
                        operations={stickyModelContentsOperations}
                        models={models}
                        selectedViewIds={selectedViewIds}
                        viewModelPermissions={viewModelPermissions}
                        focusRect={focusRect}
                      >
                        <div ref={setCanvasContainerRef} className="relative min-w-0 flex-1">
                          <FloatingUIContainer
                            setClientViewBox={setOtherUserClientViewBox}
                            onZoomIn={handleZoomIn}
                            onZoomOut={handleZoomOut}
                            onZoomReset={handleZoomReset}
                            workspace={workspace}
                            viewModel={viewModel}
                            viewModelPermissions={viewModelPermissions}
                            onGenerateLink={handleGenerateLink}
                            onExportViewModelAsImage={handleExportViewModelAsImage}
                          />

                          {/* キャンバスサイズが計測されるまでは SVG を mount しない */}
                          {contentViewBox && (
                            <GridArea
                              svgZoomPanHandlerRef={svgZoomPanHandlerRef}
                              viewModelSVGRef={viewModelSVGRef}
                              scaleExtent={scaleExtent}
                              viewBox={contentViewBox}
                              deselectElements={deselectElements}
                              onMousePointerDetected={onMousePointerDetected}
                              getGuardProps={getGuardProps}
                              onRectSelection={handleRectSelection}
                              onRectSelectionEnd={handleRectSelectionEnd}
                            >
                              {/* 整合性リンク */}
                              {viewContents && !viewContents.isEmpty() && (
                                <ConsistencyLinkCollectionView
                                  viewContents={viewContents}
                                  selectedIds={selectedConsistencyLinkIds}
                                  onSelect={handleSelectConsistencyLink}
                                  consistencyLinks={consistencyLinks}
                                />
                              )}

                              {viewModelPermissions.isContentEditable &&
                                !selectedViewIsLocked &&
                                !selectedViewIsMeaningPanel &&
                                selectedViewPosition &&
                                selectedViewSize && (
                                  /* LinkCreator は対象要素の左上が原点になっている前提で表示位置計算しているため g要素で囲む */
                                  <g transform={selectedViewPosition.toSVGTranslate()}>
                                    <LinkCreator
                                      elementSize={selectedViewSize}
                                      directions={['down', 'left', 'right']}
                                      iconOffset={LINKER_ICON_OFFSET}
                                      iconCircleSize={LINKER_ICON_CIRCLE_SIZE}
                                      onLinkerStart={handleLinkerStart}
                                      onLinkerMove={handleLinkerMove}
                                      onLinkerEnd={handleLinkerEnd}
                                      getGuardProps={getGuardProps}
                                    />
                                  </g>
                                )}

                              <PerfProfiler id="page:ViewModelPageContent">
                                {orderedViews?.map((view) => {
                                  // ひとまずモデルなしの空のビューは許容しない
                                  const model = models.get(view.modelKey);
                                  if (!model) return null;
                                  // 表示対象になっていないmeaningPanelはスキップ
                                  if (view.viewType === 'meaningPanel' && !view.meaningPanelIsOpen) return null;

                                  const modelCommentThreadId =
                                    // 旧形式: ?targetView=<viewId>&threadId=<threadId>
                                    (query.get('targetView') === view.id ? query.get('threadId') : null) ??
                                    // 新形式: ?targetCommentThread=<threadId>&targetModel=<modelId>
                                    (view.modelId === query.get('targetModel')
                                      ? query.get('targetCommentThread')
                                      : null);

                                  // 未ログインの状態ではビューをレンダリングしない
                                  if (!currentUserProfile) return null;

                                  const viewPosition = viewContents?.findPosition(view.id);
                                  if (!viewPosition) return null;

                                  const isLinkableTarget = linker?.target === view.id;

                                  const isSelected = selectedViewIds.has(view.id);

                                  return (
                                    <ViewContextProvider
                                      key={view.id}
                                      viewModelId={viewModel.id}
                                      viewId={view.id}
                                      modelId={view.modelId}
                                      viewPosition={viewPosition}
                                    >
                                      <StickyModelContentsOperationContextProvider
                                        operation={stickyModelContentsOperations[model.id]}
                                      >
                                        <PerfProfiler id={`view:${view.id}`}>
                                          <ViewView
                                            key={view.key.toString()}
                                            readonly={!viewModelPermissions.isContentEditable}
                                            ownerGroupId={workspace.groupId}
                                            viewModel={viewModel}
                                            model={model}
                                            viewId={view.id}
                                            view={view}
                                            viewPosition={viewPosition}
                                            isSelected={isSelected}
                                            onSelectSingleView={handleSelectSingleView}
                                            onToggleSelectedViews={handleToggleSelectedViews}
                                            onDeselectAllViews={handleDeselectAllViews}
                                            setMultiSelectionMode={setMultiSelectionMode}
                                            currentUserProfile={currentUserProfile}
                                            windowEventManager={windowEventManager}
                                            isLinkableTarget={isLinkableTarget}
                                            multiSelectionMode={multiSelectionMode}
                                            onClickViewModelLink={handleOnClickViewModelLink}
                                            modelCommentThreadId={modelCommentThreadId}
                                            stickyModelContentsOperation={stickyModelContentsOperations[model.id]}
                                            creatingComment={creatingComments[model.id]}
                                            onCreatingCommentAdd={handleCreatingCommentAdd}
                                            onCreatingCommentDrag={handleCreatingCommentDrag}
                                            onCreatingCommentDragEnd={handleCreatingCommentDragEnd}
                                            onCreatingCommentCancel={handleCreatingCommentCancel}
                                            onCreatingCommentSubmit={handleCreatingCommentSubmit}
                                            focusView={focusView}
                                            openDeleteModal={handleOpenDeleteViewsConfirmModal}
                                            onDragStartView={handleDragViewsStart}
                                            onDragView={handleDragViews}
                                            onDragEndView={handleDragViewsEnd}
                                            onCopyView={handleCopyView}
                                            getViewRectOperations={getViewRectOperations}
                                            onZoneToView={handleZoneToViewSelected}
                                            getGuardProps={getGuardProps}
                                            dispatch={dispatch}
                                            dragFileTargetState={dragFileTargetState}
                                          />
                                        </PerfProfiler>
                                      </StickyModelContentsOperationContextProvider>
                                    </ViewContextProvider>
                                  );
                                })}
                              </PerfProfiler>

                              {/* ビューのタイトルバーを表示するためのレイヤー */}
                              <ViewTitleBarTeleporter.Target as="g" className="focus:outline-none" />

                              <AIWorkingViewEffect viewContents={viewContents} />

                              {viewModelPermissions.isContentEditable && selectedViewPosition && selectedViewSize && (
                                <g transform={selectedViewPosition.toSVGTranslate()}>
                                  {linker && (
                                    <g
                                      transform={new Point(
                                        -VIEW_MODEL_CANVAS_SIZE / 2,
                                        -VIEW_MODEL_CANVAS_SIZE / 2
                                      ).toSVGTranslate()}
                                    >
                                      <LinkerCanvasView
                                        canvasSize={Rect.fromSize(VIEW_MODEL_CANVAS_SIZE, VIEW_MODEL_CANVAS_SIZE)}
                                        iconCircleSize={LINKER_ICON_CIRCLE_SIZE}
                                        linkerLineWidth={CONSISTENCY_LINK_LINE_WIDTH}
                                        linkerState={linker}
                                        offsetPosition={
                                          new Point(VIEW_MODEL_CANVAS_SIZE / 2, VIEW_MODEL_CANVAS_SIZE / 2)
                                        }
                                        onLinkerMove={handleLinkerMove}
                                        onLinkerEnd={handleLinkerEnd}
                                      />
                                    </g>
                                  )}
                                </g>
                              )}

                              <PopupMenuTeleporter.Target as="g" className={'focus:outline-none'} />
                              <RightClickMenuTeleporter.Target as="g" className={'focus:outline-none'} />

                              {mouseCursorPubSub.current && (
                                <ClientMouseCursorView pubSub={mouseCursorPubSub.current} />
                              )}
                            </GridArea>
                          )}

                          {!viewLoaded && viewContents && !viewContents.isEmpty() && (
                            <div className="fixed left-0 top-0 z-50 flex h-screen w-screen items-center justify-center">
                              <div className={'flex flex-col items-center rounded-lg px-5 py-2'}>
                                <FontAwesomeIcon icon={faSpinner} className="fa-spin text-3xl text-brand" />
                                <div className="mt-2 text-center text-2xl font-light text-brand">Now loading ...</div>
                              </div>
                            </div>
                          )}
                          {viewLoaded && viewContents && viewModelPermissions.isContentEditable && (
                            <ViewModelToolbar
                              readonly={!viewModelPermissions.isContentEditable}
                              selectedViewIds={selectedViewIds}
                              models={models}
                              viewContents={viewContents}
                              operations={stickyModelContentsOperations}
                              handleCreatingCommentAdd={handleCreatingCommentAdd}
                              hasCreatingComment={Object.keys(creatingComments).length > 0}
                              onAddViewClicked={handleAddStickyView}
                              onSelectView={handleSelectSingleView}
                            />
                          )}

                          {showEmptyMessageModal && viewModelPermissions.isContentEditable && (
                            <EmptyMessageModal
                              onAddBasicView={handleAddStickyView}
                              onClose={() => setShowEmptyMessageModal(false)}
                            />
                          )}
                        </div>
                        <AIAssistantDrawer
                          viewModelId={viewModel.id}
                          groupId={workspace.groupId}
                          viewModelPermissions={viewModelPermissions}
                        />
                      </AIAssistantOperationProvider>
                    )}
                  </div>
                </StickyNodeAlignIntervalProvider>
              </NewElementStyleProviderContext.Provider>
            </CommandManagerContext.Provider>
          </RightClickMenuOpenStateProvider>
        </VisibleAreaCenterPointProvider>
      </ViewModelSVGRefContextProvider>
      <ViewsDeleteConfirmModal
        isOpen={isOpenDeleteViewsConfirmModal}
        onClose={handleCloseDeleteViewsConfirmModal}
        onDelete={handleDeleteViews}
      />
      <EditingUsersLoader viewModelId={viewModel.id} />
      <ViewersLoader viewModelId={viewModel.id} />
      <FollowingIndicator />
    </Fragment>
  );
};
